import React, { useContext, useEffect, useMemo, useState } from "react";

import { Autorenew } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import mainStyles from "styles";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { UserStatus } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import UserRecord from "./components/org-user-record";
import ActivateDeactivateUsersModal from "./user-actions/activate-deactivate-users-modal";
import ActionPermissionWrapper from "components/shared/action-permission-wrapper";
import GenericDialog from "components/shared/generic-dialog";
import RPCTablePaginationActions from "components/shared/table/rpc-table-pagination-actions";
import {
  FREE_TEAM_SEAT_LIMIT,
  USER_LIST_PREFERENCE_KEY,
} from "constants/index";
import {
  OrgUsersListContext,
  UserSeatTypeFilterContext,
} from "context/organisation-context";
import {
  AdminOnlyFilterContext,
  UserQueryFilterContext,
  UserStatusFilterContext,
} from "context/organisation-context";
import { useOrgMemberCounts } from "helpers/hooks/use-org-details";
import { UrlParamTypes } from "types/org-display";

interface OrgUsersTabProps {
  handleOrgDataRefresh: () => void;
  getOrganisationUserList: (
    roleId: string,
    pageSize: number,
    offset: number,
    adminOnly: boolean,
    query: string,
    userStatus: UserStatus,
    seatType: SubscriptionSeatTypes
  ) => void;
  handleExportCsv: () => void;
  isExportingCsv: boolean;
  exportProgress?: { current: number; total: number };
  isPremiumOrg: boolean;
  isActiveSub: boolean;
}

const OrgUsersTab = ({
  handleOrgDataRefresh,
  getOrganisationUserList,
  handleExportCsv,
  isExportingCsv,
  exportProgress,
  isPremiumOrg,
  isActiveSub,
}: OrgUsersTabProps) => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const history = useHistory();
  const { orgUsersList } = useContext(OrgUsersListContext);
  const { adminOnlyFilter, setAdminOnlyFilter } = useContext(
    AdminOnlyFilterContext
  );
  const { userQueryFilter, setUserQueryFilter } = useContext(
    UserQueryFilterContext
  );
  const { userStatusFilter, setUserStatusFilter } = useContext(
    UserStatusFilterContext
  );
  const { userSeatTypeFilter, setUserSeatTypeFilter } = useContext(
    UserSeatTypeFilterContext
  );
  const { totalMembers, activeMembers } = useOrgMemberCounts();
  const classes = mainStyles();
  const [errorState, setErrorState] = useState(false);
  const [failedUsers, setFailedUsers] = useState<string[]>([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [
    openActivateDeactivateUsersModal,
    setOpenActivateDeactivateUsersModal,
  ] = useState(false);

  const showSelectCheckboxes = useMemo(() => {
    return userStatusFilter !== UserStatus.UNSPECIFIED && !adminOnlyFilter;
  }, [userStatusFilter, adminOnlyFilter]);

  const totalUsers = totalMembers;

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAll(event.target.checked);
    if (event.target.checked) {
      let allUserItems = [];
      allUserItems = orgUsersList.usersListResponse.users.map(
        (user) => user.id
      );
      setSelectedUsers(allUserItems);
      return;
    }
    setSelectedUsers([]);
  };

  const handleResultsPageUpdate = (newOffset: number) => {
    getOrganisationUserList(
      roleId,
      orgUsersList.pagination.pageSize,
      newOffset,
      adminOnlyFilter,
      userQueryFilter,
      userStatusFilter,
      userSeatTypeFilter
    );
  };

  const populateUserRecords = () => {
    if (totalUsers === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5}>
            <b>{"This organisation doesn't have any users."}</b>
          </TableCell>
        </TableRow>
      );
    }

    if (
      orgUsersList.usersListResponse.users.length === 0 &&
      userStatusFilter === UserStatus.INACTIVE
    ) {
      return (
        <TableRow>
          <TableCell colSpan={5}>
            <b>{"No deactivated users found."}</b>
          </TableCell>
        </TableRow>
      );
    }

    if (
      orgUsersList.usersListResponse.users.length === 0 &&
      userStatusFilter === UserStatus.ACTIVE
    ) {
      return (
        <TableRow>
          <TableCell colSpan={5}>
            <b>{"No active users found."}</b>
          </TableCell>
        </TableRow>
      );
    }

    if (
      orgUsersList.usersListResponse.users.length === 0 &&
      adminOnlyFilter === true
    ) {
      return (
        <TableRow>
          <TableCell colSpan={5}>
            <b>{"No admin users found."}</b>
          </TableCell>
        </TableRow>
      );
    }

    if (
      orgUsersList.usersListResponse.users.length === 0 &&
      userStatusFilter === UserStatus.UNSPECIFIED
    ) {
      return (
        <TableRow>
          <TableCell colSpan={5}>
            <b>{"No users found."}</b>
          </TableCell>
        </TableRow>
      );
    }

    return orgUsersList.usersListResponse.users.map(
      (row: User, index: number) => (
        <UserRecord
          row={row}
          key={index}
          handleRefreshOrgDisplay={() => handleOrgDataRefresh()}
          handleRefreshUserList={() => {
            getOrganisationUserList(
              roleId,
              orgUsersList.pagination.pageSize,
              orgUsersList.pagination.offset,
              adminOnlyFilter,
              userQueryFilter,
              userStatusFilter,
              userSeatTypeFilter
            );
            setSelectedUsers([]);
          }}
          isPremiumOrg={isPremiumOrg}
          isRemoveUserDialogueDisabled={totalUsers === 1}
          selected={selectedUsers.indexOf(row.id) !== -1}
          setSelected={(selected) => {
            if (selected) {
              if (selectedUsers.indexOf(row.id) === -1) {
                setSelectedUsers([...selectedUsers, row.id]);
              }
            } else {
              setSelectedUsers(selectedUsers.filter((user) => user !== row.id));
              setSelectedAll(selected);
            }
          }}
        />
      )
    );
  };

  const handleAdminOnlyToggle = () => {
    localStorage.setItem(USER_LIST_PREFERENCE_KEY, `${!adminOnlyFilter}`);
    setAdminOnlyFilter((adminOnlyFilter) => !adminOnlyFilter);
  };

  const handleOrgUserSearch = (value: string) => {
    setUserQueryFilter(value);
  };

  const handleUserStatusToogle = (
    _event: React.MouseEvent<HTMLElement>,
    newStatus: UserStatus
  ) => {
    // Prevent clicking on same button twice and setting status to null
    if (newStatus === null) {
      return;
    }

    setUserStatusFilter(newStatus);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    const newOffset = newPage * orgUsersList.pagination.pageSize;
    handleResultsPageUpdate(newOffset);
  };

  const handleRefreshUserList = () => {
    getOrganisationUserList(
      roleId,
      orgUsersList.pagination.pageSize,
      orgUsersList.pagination.offset,
      adminOnlyFilter,
      userQueryFilter,
      userStatusFilter,
      userSeatTypeFilter
    );
    setSelectedUsers([]);
  };

  useEffect(() => {
    window.localStorage.setItem(
      "user_query_pref",
      JSON.stringify(userStatusFilter)
    );
  }, [userStatusFilter]);

  return (
    <>
      <Grid container sx={classes.paddingTopSmall}>
        <TableContainer component={Paper}>
          <Table aria-label="user-list-table">
            <TableHead>
              <TableRow>
                <TableCell
                  colSpan={showSelectCheckboxes ? 6 : 5}
                  style={{ paddingLeft: "30px", paddingRight: "30px" }}
                >
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs>
                      <TextField
                        id="user-search"
                        label="User Search: Name / email only"
                        value={userQueryFilter}
                        onChange={(e) => handleOrgUserSearch(e.target.value)}
                        variant="outlined"
                        size="small"
                        style={{ width: "100%" }}
                        inputProps={{ "data-testid": "user-search" }}
                        onKeyUp={(event) => {
                          if (event.key === "Enter") {
                            return;
                          }
                        }}
                        disabled={orgUsersList.isLoading || isExportingCsv}
                      />
                    </Grid>
                    {!adminOnlyFilter ? (
                      <Grid item>
                        <ToggleButtonGroup
                          value={userStatusFilter}
                          exclusive
                          size="small"
                          onChange={handleUserStatusToogle}
                          aria-label="user status"
                          color="primary"
                          data-testid="user-status-toggle-switch"
                          disabled={orgUsersList.isLoading || isExportingCsv}
                        >
                          <ToggleButton
                            value={UserStatus.ACTIVE}
                            color="primary"
                            aria-label="active"
                            sx={classes.toogleButtonColor}
                            data-testid="user-status-toggle-switch-active"
                          >
                            Active users
                          </ToggleButton>
                          <ToggleButton
                            value={UserStatus.INACTIVE}
                            color="primary"
                            aria-label="inactive"
                            sx={classes.toogleButtonColor}
                            data-testid="user-status-toggle-switch-inactive"
                          >
                            Deactivated users
                          </ToggleButton>
                          <ToggleButton
                            value={UserStatus.UNSPECIFIED}
                            color="primary"
                            aria-label="all users"
                            sx={classes.toogleButtonColor}
                            data-testid="user-status-toggle-switch-all-users"
                          >
                            All users
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                    ) : null}
                    {isPremiumOrg ? (
                      <Grid item>
                        <FormControl sx={{ minWidth: 100 }} size="small">
                          <InputLabel id="user-seat-type-select-label">
                            Seat
                          </InputLabel>
                          <Select
                            label="Seat"
                            labelId="user-seat-type-select-label"
                            value={userSeatTypeFilter}
                            onChange={(e) => {
                              setUserSeatTypeFilter(
                                e.target.value as SubscriptionSeatTypes
                              );
                            }}
                          >
                            <MenuItem
                              value={
                                SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
                              }
                            >
                              All
                            </MenuItem>
                            <MenuItem
                              value={
                                SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM
                              }
                            >
                              Full
                            </MenuItem>
                            <MenuItem
                              value={
                                SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE
                              }
                            >
                              Lite
                            </MenuItem>
                            <MenuItem
                              value={
                                SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
                              }
                            >
                              Free
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    ) : null}
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={adminOnlyFilter}
                            onChange={handleAdminOnlyToggle}
                            name="checkedB"
                            color="primary"
                            data-testid="admins-only-toggle-switch"
                            disabled={orgUsersList.isLoading || isExportingCsv}
                          />
                        }
                        label="Admins Only"
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        onClick={handleExportCsv}
                        variant="contained"
                        color="secondary"
                        disabled={isExportingCsv}
                        data-testid="export-users-csv-button"
                        startIcon={
                          isExportingCsv ? (
                            <Autorenew className="spin-1s" />
                          ) : null
                        }
                      >
                        {isExportingCsv &&
                        exportProgress &&
                        exportProgress.total > 0
                          ? `Export CSV (${exportProgress.current}/${exportProgress.total})`
                          : "Export CSV"}
                      </Button>
                    </Grid>
                    <Grid item>
                      <ActionPermissionWrapper
                        action={
                          ActionType.ADMIN_LIST_CREATE_OR_UPDATE_USERS_JOBS
                        }
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            history.push(
                              `/organisation/${regionCode}/${roleId}/import-history`
                            )
                          }
                          data-testid="view-import-history-button"
                        >
                          Import history
                        </Button>
                      </ActionPermissionWrapper>
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
              <TableRow>
                {/* Only render if active or inactive filters are on - as activate/deactivate is the only
              bulk action available on the users list currently. */}
                {showSelectCheckboxes && (
                  <TableCell padding="checkbox" style={{ width: "2.5%" }}>
                    <Checkbox
                      checked={selectedAll}
                      disabled={
                        totalUsers === 0 ||
                        orgUsersList.usersListResponse.totalItems === 0 ||
                        orgUsersList.isLoading ||
                        isExportingCsv
                      }
                      onChange={handleSelectAllClick}
                      inputProps={
                        {
                          "data-testid": "user-table-selected-all",
                          // eslint-disable-next-line
                        } as any
                      }
                    />
                  </TableCell>
                )}
                <TableCell style={{ width: "30%", paddingLeft: "30px" }}>
                  Users
                </TableCell>
                <TableCell style={{ width: "20%" }}>Permissions</TableCell>
                <TableCell style={{ width: "10%" }}>Last Seen</TableCell>
                {isPremiumOrg && (
                  <TableCell style={{ width: "10%" }}>Seat Type</TableCell>
                )}
                <TableCell style={{ width: "5%" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgUsersList.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                populateUserRecords()
              )}
            </TableBody>
            <TableFooter>
              {showSelectCheckboxes ? (
                <TableCell colSpan={7} padding="none">
                  <Box display="flex" justifyContent="space-between">
                    <Box
                      sx={{ ...classes.spaceTopSmall, ...classes.spaceLeft }}
                    >
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{ ...classes.spaceLeft }}
                        onClick={() =>
                          setOpenActivateDeactivateUsersModal(true)
                        }
                        data-testid={"activate-or-deactivate-user-btn"}
                        disabled={
                          selectedUsers.length === 0 ||
                          !isActiveSub ||
                          // Disable activation on free teams if more than free seat limit
                          (!isPremiumOrg &&
                            userStatusFilter === UserStatus.INACTIVE &&
                            selectedUsers.length >
                              FREE_TEAM_SEAT_LIMIT - activeMembers)
                        }
                      >
                        {userStatusFilter === UserStatus.ACTIVE
                          ? "Deactivate"
                          : "Activate"}{" "}
                        Selected Users
                      </Button>
                    </Box>
                    <TablePagination
                      rowsPerPageOptions={[orgUsersList.pagination.pageSize]}
                      count={orgUsersList.usersListResponse.totalItems}
                      rowsPerPage={orgUsersList.pagination.pageSize}
                      page={Math.round(
                        orgUsersList.pagination.offset /
                          orgUsersList.pagination.pageSize
                      )}
                      component="td"
                      onPageChange={handleChangePage}
                      ActionsComponent={RPCTablePaginationActions}
                    />
                  </Box>
                </TableCell>
              ) : (
                <TablePagination
                  rowsPerPageOptions={[orgUsersList.pagination.pageSize]}
                  count={orgUsersList.usersListResponse.totalItems}
                  rowsPerPage={orgUsersList.pagination.pageSize}
                  page={Math.round(
                    orgUsersList.pagination.offset /
                      orgUsersList.pagination.pageSize
                  )}
                  component="td"
                  onPageChange={handleChangePage}
                  ActionsComponent={RPCTablePaginationActions}
                />
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </Grid>
      <ActivateDeactivateUsersModal
        open={openActivateDeactivateUsersModal}
        setOpen={setOpenActivateDeactivateUsersModal}
        setError={setErrorState}
        setFailedUsers={setFailedUsers}
        userIds={selectedUsers}
        isPremiumOrg={isPremiumOrg}
        usersCurrentStatus={userStatusFilter}
        handleRefreshOrgDisplay={handleOrgDataRefresh}
        handleRefreshUserList={() => {
          handleRefreshUserList();
        }}
      />
      <GenericDialog
        title="Failed to deactivate user(s)"
        width="sm"
        open={errorState}
        handleAckBtnClick={() => {
          setErrorState(false);
          handleOrgDataRefresh();
          handleRefreshUserList();
        }}
        handleDialogClose={() => {
          setErrorState(false);
          handleOrgDataRefresh();
          handleRefreshUserList();
        }}
        hasAckBtn={true}
        acknowledgeBtnLabel="OK"
      >
        Failed user(s): {failedUsers.join(", ")}
      </GenericDialog>
    </>
  );
};

export default OrgUsersTab;
